import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface PaymentPageProps {}

const PaymentPage: React.FC<PaymentPageProps> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  // Plan details mapping - memoized to avoid dependency issues
  const planDetails = useMemo(() => ({
    '3_months': {
      name: '3 Months Plan',
      price: 300,
      duration: 3,
      description: 'Perfect for trying out Knoozy'
    },
    '6_months': {
      name: '6 Months Plan',
      price: 500,
      duration: 6,
      description: 'Most popular choice',
      savings: 100
    },
    '12_months': {
      name: '12 Months Plan',
      price: 800,
      duration: 12,
      description: 'Best value for dedicated learners',
      savings: 400
    }
  }), []);

  useEffect(() => {
    if (planId && planDetails[planId as keyof typeof planDetails]) {
      setSelectedPlan(planDetails[planId as keyof typeof planDetails]);
    } else {
      navigate('/dashboard');
    }
  }, [planId, navigate, planDetails]);

  const handlePayment = async () => {
    if (!planId) return;

    setLoading(true);
    setError(null);

    try {
      // For now, we'll just show a placeholder
      // In Sprint 5, we'll integrate with Razorpay
      alert('Payment integration will be added in Sprint 5. For now, subscription has been activated for demo purposes.');

      // Mock subscription activation (remove this in production)
      // You would normally call the payment gateway here
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedPlan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-8 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Complete Your Subscription</h1>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Order Summary */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{selectedPlan.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{selectedPlan.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-medium">{selectedPlan.duration} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Price</span>
                      <span className="font-medium">₹{selectedPlan.price}</span>
                    </div>
                    {selectedPlan.savings && (
                      <div className="flex justify-between text-green-600">
                        <span>You Save</span>
                        <span className="font-medium">₹{selectedPlan.savings}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total Amount</span>
                      <span className="text-indigo-600">₹{selectedPlan.price}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">What's Included</h3>
                <ul className="space-y-2">
                  {[
                    'Unlimited article access',
                    'Age-appropriate content',
                    'Daily quizzes',
                    'Progress tracking',
                    'Expert-curated articles',
                    'Ad-free reading experience'
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Payment Form */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Method</h2>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> Payment gateway integration coming soon in Sprint 5.
                  Click proceed to activate a demo subscription.
                </p>
              </div>

              {/* Payment Options (placeholder for now) */}
              <div className="space-y-3">
                <div className="border-2 border-indigo-200 rounded-xl p-4 cursor-pointer hover:border-indigo-400 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CreditCardIcon className="h-6 w-6 text-gray-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Razorpay</p>
                        <p className="text-sm text-gray-600">UPI, Cards, Net Banking</p>
                      </div>
                    </div>
                    <div className="w-4 h-4 rounded-full border-2 border-indigo-500 bg-indigo-500"></div>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="text-xs text-gray-500">
                By proceeding with the payment, you agree to our Terms of Service and Privacy Policy.
                Subscription will auto-renew unless cancelled.
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handlePayment}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Processing...' : `Pay ₹${selectedPlan.price}`}
                </button>

                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Security Badge */}
          <div className="mt-8 pt-6 border-t text-center">
            <div className="flex items-center justify-center text-gray-600 text-sm">
              <svg className="h-5 w-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>100% Secure Payment • SSL Encrypted</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;